import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HeroCard } from '@/components/HeroCard';
import { StatRow, type StatRowItem } from '@/components/StatRow';
import { QuickAction } from '@/components/QuickAction';
import { ReviewRequestSheet } from '@/components/ReviewRequestSheet';
import { ActivityFeed } from '@/components/ActivityFeed';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonToday } from '@/components/Skeleton';
import { useToday } from '@/hooks/useToday';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator';
import { WeatherCard } from '@/components/WeatherCard';

/** "Friday, May 1" — Intl, no date-fns dep. (Time-of-day greeting removed per
 *  Ernie 2026-05-21 — just the date heads the Today view now.) */
function formatTodayDate(now: Date): string {
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

const reviewIcon = (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const attentionIcon = (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
  </svg>
);

function formatDollars(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// 2026-07-02 (DemoPolish 1) — bookings clause REMOVED: it's the manual
// Mark-Booked metric ("0 bookings" undersold a good day). Only system-
// derived numbers on the hero: quotes sent + needs-your-eyes.
function buildHeroSub(quotes: number, attention: number): string {
  // "4 quotes sent · 1 needs your eyes"
  const parts: string[] = [];
  if (quotes > 0) {
    parts.push(`${quotes} ${quotes === 1 ? 'quote' : 'quotes'} sent`);
  }
  if (attention > 0) {
    parts.push(`${attention} need${attention === 1 ? 's' : ''} your eyes`);
  }
  // Calls happened but nothing quoted / nothing pending — still say
  // something true and positive rather than an empty line.
  return parts.length > 0 ? parts.join(' · ') : 'all handled by Shelby';
}

/**
 * Today — the SS PWA's home tab. Greeting + hero + stat row + quick actions
 * + recent activity feed. Critical-path per feedback_pwa_critical_path.md.
 */
export function Today() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const todayQuery = useToday(slug);

  const dateLabel = useMemo(() => formatTodayDate(new Date()), []);
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false);

  // Pull-to-refresh on Today (Ernie 2026-05-03 — uses native touch events)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { pullDistance, refreshing, triggerPx } = usePullToRefresh({
    scrollContainerRef,
    onRefresh: () => todayQuery.refetch(),
  });

  const goToConversations = (filter: string | null) => {
    const search = filter ? `?filter=${filter}` : '';
    navigate(`/${slug}/conversations${search}`);
  };

  // --- Render branches ---------------------------------------------------
  if (todayQuery.isLoading) {
    return <SkeletonToday />;
  }

  if (todayQuery.isError || !todayQuery.data) {
    return (
      <div className="bg-ink-50">
        <ErrorState
          title="Couldn’t load today"
          message={
            todayQuery.error instanceof Error
              ? todayQuery.error.message
              : 'Please try again.'
          }
          onRetry={() => void todayQuery.refetch()}
        />
      </div>
    );
  }

  const today = todayQuery.data;

  const stats: StatRowItem[] = [
    {
      value: formatDollars(today.active_quotes_dollars),
      numericValue: today.active_quotes_dollars,
      currency: true,
      label: 'Active quotes',
      onClick: () => goToConversations('quoted'),
      ariaLabel: 'View quoted conversations',
    },
    {
      // Distinct callers in the last 7 days, sourced from the contact's
      // voice_ai_call_at field. Falls back to today's activity total when
      // the Worker is an older build that didn't return calls_this_week.
      value: String(
        today.calls_this_week ??
          today.quotes_sent + today.bookings + today.calls_today,
      ),
      numericValue:
        today.calls_this_week ??
        today.quotes_sent + today.bookings + today.calls_today,
      label: 'Calls this week',
      onClick: () => goToConversations('called'),
      ariaLabel: 'View conversations from calls this week',
    },
    {
      value: String(today.needs_attention),
      numericValue: today.needs_attention,
      label: 'Needs you',
      attention: true,
      onClick: () => goToConversations('needs'),
      ariaLabel: 'View conversations that need you',
    },
  ];

  const attentionCount = today.needs_attention;
  const attentionSub =
    attentionCount === 1
      ? 'Shelby flagged a callback request'
      : attentionCount === 0
        ? 'You’re all caught up'
        : `Shelby flagged ${attentionCount} threads`;

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain bg-ink-50 dark:bg-[#0B1220]"
      style={{
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Pull-to-refresh — in-flow circular spinner (Apple-style).
          Sits as the FIRST child so its growing height naturally pushes
          all the content below it DOWN, exactly like Mail/Insta/X. */}
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        refreshing={refreshing}
        triggerPx={triggerPx}
      />

      {/* Date + entry-point to Settings (gear icon, top-right) */}
      <div className="flex items-start justify-between px-5 pb-1 pt-4">
        <strong className="block text-[26px] font-bold tracking-tight text-ink-900 dark:text-[#E2E8F0]">
          {dateLabel}
        </strong>
        <button
          type="button"
          onClick={() => navigate(`/${slug}/settings`)}
          className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full text-ink-400 dark:text-[#64748B] active:bg-ink-100 dark:active:bg-[#1E293B]"
          aria-label="Settings"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>

      {/* Weather — NOAA/NWS card for Wallingford CT, rendered above the hero.
          Silent graceful degrade on NWS error (renders nothing). 2026-06-04. */}
      <WeatherCard />

      {/* Hero — three modes:
            - Zero state (nothing today + no attention): dark "command
              center" with system status dots, dodges bleak 0/0/0 vibe
            - Needs you (attention > 0): blue card leading with action
            - Active (calls happened): blue card with count-up
          R11 consult 2026-05-06. */}
      {(() => {
        const isZero =
          today.calls_today === 0 &&
          today.quotes_sent === 0 &&
          today.bookings === 0 &&
          today.needs_attention === 0;
        return (
          <HeroCard
            label="Today, Shelby handled"
            numericValue={today.calls_today}
            unit="call"
            sub={buildHeroSub(today.quotes_sent, today.needs_attention)}
            needsAttention={today.needs_attention}
            isZeroState={isZero}
          />
        );
      })()}

      {/* Removed the ImpactStrip "In your pipeline" callout — Ernie
          2026-05-12: redundant with the Active quotes stat tile below
          and ate too much vertical space. */}

      {/* 3-card stat row — each tile navigates to the matching
          Conversations filter when tapped. */}
      <StatRow items={stats} />

      {/* Quick actions — section title + 2 rows */}
      <div className="flex items-center justify-between px-5 pb-2 pt-2 text-[13px] font-semibold uppercase tracking-wider text-ink-500">
        Quick actions
      </div>
      {/* 2026-07-02 (DemoPolish 2) — real send flow (was a dead nav to Chats):
          searchable recent-customer picker OR manual number, explicit confirm,
          then the existing locked review-request send path. */}
      <QuickAction
        icon={reviewIcon}
        title="Send review request"
        sub="Pick a customer or enter a number"
        onClick={() => setReviewSheetOpen(true)}
      />
      <QuickAction
        icon={attentionIcon}
        title={
          attentionCount === 1
            ? '1 conversation needs you'
            : attentionCount === 0
              ? 'Nothing needs you'
              : `${attentionCount} conversations need you`
        }
        sub={attentionSub}
        onClick={() => goToConversations('needs')}
        attention={attentionCount > 0}
      />

      {/* Recent activity */}
      <div className="flex items-center justify-between px-5 pb-2 pt-2 text-[13px] font-semibold uppercase tracking-wider text-ink-500">
        Recent activity
        <button
          type="button"
          onClick={() => goToConversations(null)}
          className="text-[13px] font-semibold normal-case tracking-normal text-ss-blue"
        >
          See all
        </button>
      </div>
      <ActivityFeed items={today.recent_activity} limit={5} />

      <ReviewRequestSheet slug={slug} open={reviewSheetOpen} onClose={() => setReviewSheetOpen(false)} />
    </div>
  );
}
